import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Barbell,
    Trophy,
    Clock,
    ChartBar,
    Lightning,
    ArrowRight,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { timeAgo, dayLabel, duration } from '@/lib/time';
import { CalendarHeatmap } from '@/components/CalendarHeatmap';
import { HISTORY } from '@/constants/testIds';

export default function History() {
    const { user } = useAuth();
    const units = user?.units || 'kg';
    const [sessions, setSessions] = useState(null);
    const [heatmap, setHeatmap] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [list, heat] = await Promise.all([
                    api.get('/sessions/history/list', {
                        params: { limit: 100 },
                    }),
                    api.get('/insights/heatmap', { params: { weeks: 16 } }),
                ]);
                if (!cancelled) {
                    setSessions(list.data);
                    setHeatmap(heat.data);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        formatApiErrorDetail(e.response?.data?.detail) ||
                            e.message
                    );
                    setSessions([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const totals = useMemo(() => {
        if (!sessions) return null;
        const completed = sessions.filter((s) => s.completed_at);
        const totalVolume = sessions.reduce(
            (acc, s) => acc + (s.total_volume || 0),
            0
        );
        const totalPRs = sessions.reduce(
            (acc, s) => acc + (s.pr_count || 0),
            0
        );
        return {
            count: sessions.length,
            completedCount: completed.length,
            totalVolume,
            totalPRs,
        };
    }, [sessions]);

    return (
        <main
            data-testid={HISTORY.root}
            className="mx-auto max-w-4xl px-5 sm:px-8 pt-10 sm:pt-14 pb-32"
        >
            <div className="animate-fade-up">
                <span className="uppercase-mono text-muted-foreground">
                    Timeline
                </span>
                <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl">
                    History
                </h1>
                <p className="mt-3 text-muted-foreground max-w-xl">
                    Every session you've started — neon for PRs, muted for
                    in-progress. Tap any row to revisit the log.
                </p>
            </div>

            {totals && (
                <section className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KeyStat
                        icon={<Barbell size={16} weight="bold" />}
                        label="Sessions"
                        value={totals.count}
                    />
                    <KeyStat
                        icon={<Lightning size={16} weight="bold" />}
                        label="Completed"
                        value={totals.completedCount}
                    />
                    <KeyStat
                        icon={<ChartBar size={16} weight="bold" />}
                        label="Volume"
                        value={`${Math.round(totals.totalVolume).toLocaleString()} ${units}`}
                    />
                    <KeyStat
                        icon={<Trophy size={16} weight="fill" />}
                        label="PRs"
                        value={totals.totalPRs}
                        accent="#FF4D4D"
                    />
                </section>
            )}

            {/* Heatmap — last 16 weeks of completed sets. */}
            <section className="mt-8 glass-card rounded-3xl p-5 sm:p-6 overflow-x-auto">
                <CalendarHeatmap days={heatmap} weeks={16} units={units} />
            </section>

            {error && (
                <p className="mt-6 text-sm text-[#FF4D4D] font-medium">
                    {error}
                </p>
            )}

            <section className="mt-10">
                {sessions === null ? (
                    <p className="uppercase-mono text-muted-foreground animate-soft-pulse">
                        Loading…
                    </p>
                ) : sessions.length === 0 ? (
                    <EmptyState />
                ) : (
                    <ul
                        data-testid={HISTORY.list}
                        className="space-y-3"
                    >
                        {sessions.map((s) => (
                            <SessionRow key={s.id} session={s} units={units} />
                        ))}
                    </ul>
                )}
            </section>
        </main>
    );
}

function KeyStat({ icon, label, value, accent }) {
    return (
        <div className="glass-card rounded-2xl px-4 py-3">
            <div className="flex items-center justify-between mb-1.5">
                <span className="uppercase-mono text-muted-foreground">
                    {label}
                </span>
                <span
                    className="grid place-items-center w-6 h-6 rounded-full"
                    style={{
                        background: accent ? `${accent}22` : 'rgba(127,127,127,0.12)',
                        color: accent || 'currentColor',
                    }}
                >
                    {icon}
                </span>
            </div>
            <p
                className="font-mono text-xl font-bold tabular-nums tracking-tight"
                style={{ color: accent || undefined }}
            >
                {value}
            </p>
        </div>
    );
}

function SessionRow({ session, units }) {
    const isCompleted = !!session.completed_at;
    const dur = duration(session.started_at, session.completed_at);
    return (
        <li
            data-testid={`${HISTORY.item}-${session.id}`}
            className="glass-card rounded-2xl"
        >
            <Link
                to={`/sessions/${session.id}`}
                data-testid={`${HISTORY.itemLink}-${session.id}`}
                className="block p-4 sm:p-5 group"
            >
                <div className="flex items-start gap-4">
                    <div
                        className={`shrink-0 grid place-items-center w-12 h-12 rounded-2xl border ${
                            isCompleted
                                ? 'bg-[#00FF9D]/10 border-[#00FF9D]/30 text-[#00FF9D]'
                                : 'bg-foreground/5 border-foreground/10 text-muted-foreground'
                        }`}
                    >
                        {isCompleted ? (
                            <Lightning size={18} weight="fill" />
                        ) : (
                            <Clock size={18} weight="bold" />
                        )}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="uppercase-mono text-muted-foreground">
                                {dayLabel(session.started_at)}
                            </span>
                            <span className="uppercase-mono text-muted-foreground">
                                ·
                            </span>
                            <span className="uppercase-mono text-muted-foreground">
                                {timeAgo(session.started_at)}
                            </span>
                            {!isCompleted && (
                                <span className="uppercase-mono inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-foreground/15 text-muted-foreground ml-1">
                                    in progress
                                </span>
                            )}
                        </div>
                        <h3 className="font-display font-extrabold tracking-tight text-xl sm:text-2xl truncate group-hover:underline underline-offset-4">
                            {session.name}
                        </h3>

                        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                            <Inline
                                label="Exercises"
                                value={session.exercise_count || 0}
                            />
                            <Inline
                                label="Sets"
                                value={session.set_count || 0}
                            />
                            <Inline
                                label="Volume"
                                value={`${Math.round(session.total_volume || 0).toLocaleString()} ${units}`}
                                highlight
                            />
                            {dur && <Inline label="Duration" value={dur} />}
                            {session.pr_count > 0 && (
                                <span className="uppercase-mono inline-flex items-center gap-1 text-[#FF4D4D]">
                                    <Trophy size={11} weight="fill" />
                                    {session.pr_count} PR
                                    {session.pr_count > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>

                    <ArrowRight
                        size={18}
                        weight="bold"
                        className="text-muted-foreground self-center shrink-0 group-hover:text-foreground group-hover:translate-x-0.5 transition-all"
                    />
                </div>
            </Link>
        </li>
    );
}

function Inline({ label, value, highlight }) {
    return (
        <span className="inline-flex items-baseline gap-1.5">
            <span className="uppercase-mono text-muted-foreground">
                {label}
            </span>
            <span
                className={`font-mono text-sm font-bold tabular-nums ${
                    highlight ? 'text-[#00FF9D]' : ''
                }`}
            >
                {value}
            </span>
        </span>
    );
}

function EmptyState() {
    return (
        <div
            data-testid={HISTORY.empty}
            className="glass-card rounded-3xl p-12 text-center"
        >
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-foreground/5 border border-foreground/10 mx-auto mb-5">
                <Clock size={22} weight="bold" />
            </span>
            <h3 className="font-display font-extrabold tracking-tight text-2xl">
                No sessions yet
            </h3>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">
                Start a workout from a template — every set you log will show
                up here.
            </p>
            <Link
                to="/workouts"
                className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
            >
                Browse templates
                <ArrowRight size={16} weight="bold" />
            </Link>
        </div>
    );
}
